import { ISortOption } from '@types'
import clsx from 'clsx'
import LoadingOverlay from 'components/common/LoadingOverlay'
import HomeCategoryMobileMenu from 'components/home/HomeCategoryMobileMenu'
import ArrowUpIcon from 'components/icons/ArrowUpIcon'
import ProductCard from 'components/product/ProductCard'
import ProductModal from 'components/product/ProductCard/ProductModal'
import { DEFAULT_LIMIT } from 'config/constants'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useMenus from 'hooks/useMenus'
import useSlug from 'hooks/useSlug'
import {
  isAbsent,
  isEmptyArray,
  isEmptyObj,
  isEmptyString,
  isLengthGt,
  isNonEmptyArray,
  isNonEmptyString,
  isPresent,
  isSame,
} from 'lib/utils'

import Head from 'next/head'
import { CategoryPageProps } from 'pages/categories/[...slug]'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import {
  IProduct,
  selectCategoryList,
  selectProductList,
} from 'store/api'
import { selectIsBrowser, selectIsSm } from 'store/ui'
import CategoryDescription from './CategoryDescription'
import { getSeoH1 } from 'config/seoH1'
import CategoryDetails from './CategoryDetails'
import CategoryLeftMenu from './CategoryLeftMenu'
// import CategorySortBy from './CategorySortBy'
import Alerts from 'components/common/Alerts'
import WideAltCard from 'components/product/ProductCard/WideAltCard'
import useCart from 'hooks/useCart'
import CategorySubs from './CategorySubs'

let CategoryView = ({
  categoryListSSR,
  productListSSR,
  menus: menusSSR,
}: CategoryPageProps) => {
  let { slug, queryLng, router } = useSlug()

  // const scrollToProducts = useCallback(() => {
  //   setTimeout(() => {
  //     window.scrollTo({
  //       top: 250,
  //     })
  //   }, 200)
  // }, [])

  // useIsomorphicLayoutEffect(() => {
  //   if (slug?.length === 2 && isLg) {
  //     window.scrollTo({
  //       top: 250,
  //     })
  //   }
  // }, [slug])

  let [endIdx, setEndIdx] = useState<number>(DEFAULT_LIMIT)
  let [sortBy, setSortBy] = useState<ISortOption>('popularity')

  let [product, setProduct] = useState<IProduct | null>(null)
  let [isProductModalOpen, setIsProductModalOpen] = useState(false)

  let _products = useSelector(selectProductList(queryLng))
  let _categoryList = useSelector(selectCategoryList(queryLng))
  let isBrowser = useSelector(selectIsBrowser)

  // let isLg = useSelector(selectIsLg)
  let isSm = useSelector(selectIsSm)

  useIsomorphicLayoutEffect(() => {
    setSortBy('popularity')
    setEndIdx(DEFAULT_LIMIT)
  }, [slug])

  let { leftMenu } = useMenus(menusSSR)

  let activeCategoryList = useMemo(
    () =>
      isNonEmptyArray(_categoryList)
        ? // ? (toKeys(_products)
          //     .map((id) =>
          //       _categoryList?.find((c) => isSame(c.id, Number(id)))
          //     )
          //     .filter((x) => isPresent(x)) as ICategory[])
          _categoryList
        : isBrowser
        ? []
        : categoryListSSR,
    [_categoryList, categoryListSSR, isBrowser]
  )

  let categoryMain = useMemo(
    () => activeCategoryList.find((x) => isSame(x.slug, slug![0])),
    [activeCategoryList, slug]
  )

  let categorySub = useMemo(
    () =>
      categoryMain !== undefined
        ? categoryMain.sub.find((x) => isSame(x.slug, slug![1]))
        : undefined,
    [categoryMain, slug]
  )

  let title = useMemo(
    () =>
      categorySub
        ? categorySub?.meta.title
        : categoryMain?.meta.title || categoryMain?.name,
    [categoryMain, categorySub]
  )

  let productList = useMemo(() => {
    let products = isPresent(categorySub)
      ? _products?.[categorySub.id] ?? productListSSR ?? []
      : isPresent(categoryMain)
      ? _products?.[categoryMain.id] ?? productListSSR ?? []
      : productListSSR ?? []

    return products

    // return isSame(sortBy, 'popularity')
    //   ? products.slice().sort((a, b) => b.id - a.id)
    //   : products.slice().sort((a, b) => a.price - b.price)
  }, [_products, categoryMain, categorySub, productListSSR, sortBy])

  const [lowPrice, highPrice] = useMemo(() => {
    const sorted = productList
      ?.slice()
      .sort((a, b) => a.price - b.price)
    if (sorted.length > 1)
      return [sorted[0]?.price, sorted[sorted.length - 1]?.price]
    else return [0, 0]
  }, [productList])

  let productListSliced: IProduct[] = useMemo(
    () => productList.slice(0, endIdx),
    [endIdx, productList]
  )

  let subs = useMemo(
    () =>
      (categoryMain?.sub ?? []).filter((categorySub) =>
        isNonEmptyArray(
          (_products ?? productListSSR)?.[categorySub.id]
        )
      ),
    [categoryMain?.sub, _products, productListSSR]
  )

  // let handleSortClick = useCallback((x: ISortOption) => {
  //   setSortBy(x)
  //   setEndIdx(DEFAULT_LIMIT)
  // }, [])

  // useIsomorphicLayoutEffect(() => {
  //   if (
  //     isAbsent(categoryMain) &&
  //     isNonEmptyArray(activeCategoryList)
  //   ) {
  //     void router.replace('/')
  //   }
  // }, [activeCategoryList, categoryMain, router, query])

  useEffect(() => {
    if (productList.length > 0 && router.query['product']) {
      const id = router.query['product']
      const searchedProduct = productList.find((p) => p.id === +id)
      if (isPresent(searchedProduct)) {
        setProduct(searchedProduct)
        setIsProductModalOpen(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query])

  let handleModalOpen = useCallback((product: IProduct) => {
    setProduct(product)
    setIsProductModalOpen(true)
  }, [])

  const closeProductModal = useCallback(() => {
    setIsProductModalOpen(false)
    setTimeout(() => {
      setProduct(null)
    }, 500)
  }, [])

  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isAbsent(bottomRef.current)) return

    const cachedRef = bottomRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && isLengthGt(endIdx, productList))
          setEndIdx((prev) => prev + DEFAULT_LIMIT)
      },
      { threshold: 1 }
    )

    observer.observe(cachedRef)
    return () => observer.unobserve(cachedRef)
  }, [bottomRef, productList, endIdx])

  const { items } = useCart()

  const isAltMobileCategory =
    categoryMain?.id === 1 || categoryMain?.slug === 'novyij_god'

  const renderItem = useCallback(
    (p: IProduct) => {
      const _item = items.find((i) => i.product.id === p.id)
      const isSimpleProduct =
        (p.type === 'simple' || p.variations_count == 1) &&
        isAbsent(p.options?.ingsgroups)

      const quantity = isPresent(_item)
        ? isSimpleProduct
          ? _item.quantity
          : items.reduce((count, i) => {
              if (i.product.id === p.id) count += i.quantity
              return count
            }, 0)
        : 0

      const CardComponent =
        isSm && isAltMobileCategory ? WideAltCard : ProductCard

      return (
        <CardComponent
          {...p}
          items={items}
          quantity={quantity}
          key={p.id}
          descClassName='font-light'
          cb={() => handleModalOpen(p)}
          categorySlug={slug![0] ?? ''}
        />
      )
    },
    [handleModalOpen, slug, items, isSm, isAltMobileCategory]
  )

  if (
    isAbsent(categoryMain) ||
    isEmptyArray(productList) ||
    isEmptyArray(activeCategoryList)
  )
    return (
      <LoadingOverlay
        className='h-5 w-5 mx-2.5 bg-accent'
        fixed
        open
      />
    )

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name='apple-mobile-web-app-title' content={title} />
        <meta property='og:site_name' content={title} />
        <meta
          name='description'
          content={
            categorySub
              ? categorySub?.meta.description
              : categoryMain?.meta.description
          }
        />
        <meta
          name='keywords'
          content={
            categorySub
              ? categorySub?.meta.keywords
              : categoryMain?.meta.keywords
          }
        />
      </Head>

      <>
        <HomeCategoryMobileMenu
          categoryList={activeCategoryList}
          leftMenu={leftMenu}
        />

        <Alerts type={`category.${categoryMain?.path ?? ''}`} />

        <div itemScope itemType='https://schema.org/Product'>
          <CategoryDescription
            name={categoryMain.name}
            h1={getSeoH1(`/categories/${categoryMain.slug}`, categoryMain.name)}
          />

          <div
            itemType='http://schema.org/AggregateOffer'
            itemScope
            itemProp='offers'
            className='custom-plx2'
          >
            <meta
              content={productList?.length.toString()}
              itemProp='offerCount'
            />
            <meta
              content={lowPrice?.toString()}
              itemProp='lowPrice'
            />
            <meta
              content={highPrice?.toString()}
              itemProp='highPrice'
            />
            <meta content='RUB' itemProp='priceCurrency' />

            <div className='flex items-start relative lg:bg-lightGrey rounded-2xl lg:py-2 lg:pr-12 lg:mb-4'>
              <CategoryLeftMenu
                // scrollToProducts={scrollToProducts}
                leftMenu={leftMenu}
                activeCategoryList={activeCategoryList}
              />

              <div className='w-full'>
                {isNonEmptyArray(subs) && (
                  <CategorySubs {...categoryMain} sub={subs} />
                )}

                <div className='mt-4 md:mt-2 flex flex-col md:flex-row justify-end md:items-center'>
                  {/* <CategorySortBy
                  cb={handleSortClick}
                  sortBy={sortBy}
                /> */}
                </div>

                {isNonEmptyArray(productListSliced) ? (
                  <div
                    className={clsx(
                      `grid gap-1 grid-cols-2 md:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-${
                        isAltMobileCategory ? 3 : 4
                      }`,
                      {
                        'mb-24':
                          isAbsent(categoryMain.text) ||
                          isEmptyString(categoryMain.text),
                        'mb-10':
                          isPresent(categoryMain.text) &&
                          isNonEmptyString(categoryMain.text),
                      }
                    )}
                  >
                    {productListSliced.map((x: IProduct) =>
                      renderItem(x)
                    )}
                  </div>
                ) : (
                  <div className='relative mt-8 py-2 text-4xl leading-snug text-center tracking-tighter'>
                    {isEmptyObj(_products) ? (
                      <LoadingOverlay
                        open
                        className='h-4 w-4 mx-1.5 bg-accent'
                      />
                    ) : (
                      'Нет подходящих блюд'
                    )}
                  </div>
                )}

                <div ref={bottomRef}></div>

                {isPresent(categoryMain.text) &&
                  isNonEmptyString(categoryMain.text) && (
                    <CategoryDetails category={categoryMain} />
                  )}
              </div>
            </div>
          </div>
        </div>

        <ProductModal
          isProductModalOpen={isProductModalOpen}
          closeProductModal={closeProductModal}
          product={product}
        />

        <div className='hidden lg:block absolute top-8 bottom-0 z-50 right-1 pointer-events-none'>
          <div className='h-full max-h-screen sticky top-2 pb-4 flex flex-col justify-end'>
            <button
              type='button'
              className='w-[3.75rem] h-[3.75rem] group rounded-full p-4 bg-primary hover:bg-darkYellow pointer-events-auto'
              onClick={() => window.scrollTo({ top: 0 })}
            >
              <ArrowUpIcon className='w-full h-full text-dark' />
            </button>
          </div>
        </div>
      </>
    </>
  )
}

export default CategoryView
